import { id, Identifiable, list, List } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { reduceTask } from "../task";
import { reduceMilestone } from "../milestone";
import { reduceEvent } from "../event";
import {
  handleEditRequest,
  handleEditSuccess,
  handleEditFailure,
} from "./editList";
import { handleEditTask } from "./editTask";
import { handleEditMilestone } from "./editMilestone";
import { handleEditEvent } from "./editEvent";
import {
  handleEditProjectRequest,
  handleEditProjectSuccess,
  handleEditProjectFailure,
} from "./editProject";
import { handleCreateTask } from "./createTask";
import { handleCreateMilestone } from "./createMilestone";
import { handleCreateList } from "./createList";
import { handleCreateEvent } from "./createEvent";
import { handleRemoveTask } from "./removeTask";
import { handleRemoveList } from "./removeList";
import { handleRemoveMilestone } from "./removeMilestone";
import { handleRemoveEvent } from "./removeEvent";
import {
  handleMoveListElementRequest,
  handleMoveListElementSuccess,
  handleMoveListElementFailure,
} from "./moveListElement";
import {
  handleAssignTagRequest,
  handleAssignTagSuccess,
  handleAssignTagFailure,
} from "./assignTag";
import {
  handleRemoveTagFailure,
  handleRemoveTagSuccess,
  handleRemoveTagRequest,
} from "./removeTag";
import { handleConstrainElements } from "./constrainElement";
import { handleDuplicateList } from "./duplicateList";
import { handleMarkTaskAsCompleted } from "./markTaskAsCompleted";
import { handleEditConstraint } from "./editConstraint";

const initialState = { ...list, uuid: id };

function propagateEvent(
  list: Identifiable<List> = initialState,
  event: Event,
): Identifiable<List> {
  return {
    ...list,
    tasks: list.tasks ? list.tasks.map((task) => reduceTask(task, event)) : [],
    milestones: list.milestones
      ? list.milestones.map((milestone) => reduceMilestone(milestone, event))
      : [],
    events: list.events ? list.events.map((e) => reduceEvent(e, event)) : [],
    subLists: list.subLists
      ? list.subLists.map((subList) => ({
          ...subList,
          ...propagateEvent(subList, event),
        }))
      : [],
  };
}

const reduceList = createStateReducer(
  initialState,
  {
    [events.editListRequested]: handleEditRequest,
    [events.editListSucceeded]: handleEditSuccess,
    [events.editListFailed]: handleEditFailure,
    [events.createTaskSucceeded]: handleCreateTask,
    [events.createKanbanTaskSucceeded]: handleCreateTask,
    [events.createListSucceeded]: handleCreateList,
    [events.createMilestoneSucceeded]: handleCreateMilestone,
    [events.createEventSucceeded]: handleCreateEvent,
    [events.removeTaskSucceeded]: handleRemoveTask,
    [events.removeListSucceeded]: handleRemoveList,
    [events.removeMilestoneSucceeded]: handleRemoveMilestone,
    [events.removeEventSucceeded]: handleRemoveEvent,
    [events.editTaskSucceeded]: handleEditTask,
    [events.editMilestoneSucceeded]: handleEditMilestone,
    [events.editEventSucceeded]: handleEditEvent,
    [events.editProjectRequested]: handleEditProjectRequest,
    [events.editProjectSucceeded]: handleEditProjectSuccess,
    [events.editProjectFailed]: handleEditProjectFailure,
    [events.moveListElementRequested]: handleMoveListElementRequest,
    [events.moveListElementSucceeded]: handleMoveListElementSuccess,
    [events.moveListElementFailed]: handleMoveListElementFailure,
    [events.assignTagRequested]: handleAssignTagRequest,
    [events.assignTagSucceeded]: handleAssignTagSuccess,
    [events.assignTagFailed]: handleAssignTagFailure,
    [events.removeTagRequested]: handleRemoveTagRequest,
    [events.removeTagSucceeded]: handleRemoveTagSuccess,
    [events.removeTagFailed]: handleRemoveTagFailure,
    [events.constrainElementsSucceeded]: handleConstrainElements,
    [events.duplicateListSucceeded]: handleDuplicateList,
    [events.editConstraintSucceeded]: handleEditConstraint,
    [events.markTaskAsCompletedSucceeded]: handleMarkTaskAsCompleted,
  },
  propagateEvent,
);

export { reduceList };
